import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { headerManager } from "@/lib/requests";
import { api, types } from "@/lib/openapi";
import { useToast } from "primevue/usetoast";
import { baseSuccessToastMessage } from "@/primevue/init";

import { defineStore, storeToRefs } from "pinia";
import { useOrganizationManager } from "@/composables/organization-manager";
import { Stek, useSteksManager, useSteksStore } from "@/stores/steks-store";

import { useSyncStore } from "@/stores/sync-store";
import {
    EntityInfo,
    EntityType,
    useCurrentEntityManager,
} from "@/stores/entity-store";
import { throttle } from "lodash";
import { useEditManager } from "@/composables/edit-manager";
import { syncChannel } from "@/lib/broadcast-channels";
import { Artefact } from "@/lib/artefacts";
import { FILE_SYNC_THROTTLE_MS } from "@/composables/draft-manager";
import { followLink } from "@/lib/openapi/helpers";

export const useStekStore = defineStore("stek", () => {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const { organization } = useOrganizationManager();
    const { steks, isLoading: isLoadingSteks } = useSteksManager();
    const { getSteks } = useSteksStore();
    const isFetchingStek = ref(false);
    const logo = ref<Artefact>();
    const fqdn = ref<string>();
    const originalFqdn = ref<string>();
    const isFetchingLogo = ref(false);
    const controller = ref<AbortController>(new AbortController());
    const stek = ref<Stek | undefined>();
    const stekInfo = ref<EntityInfo | undefined>();

    onMounted(() => {
        const { stek: localStekInfo } = useCurrentEntityManager();
        watch(
            localStekInfo,
            (newStekInfo) => {
                stekInfo.value = newStekInfo;
            },
            { immediate: true }
        );
    });

    const isFetching = computed(() => {
        return isFetchingStek.value || isFetchingLogo.value;
    });

    const showSelectStek = ref(false);

    const isModified = computed(() => {
        return Boolean(
            logo.value?.isModified || fqdn.value !== originalFqdn.value
        );
    });

    async function selectStekFromSteks() {
        if (!steks.value) return;
        if (!route.meta.requiresStek) {
            stek.value = undefined;
            return;
        }

        // When there is no selected organization, there is no way to
        // select the current stek.
        if (!route.params.orgName) {
            showSelectStek.value = false;
            return;
        }

        const stekName = route.params.stekName;
        if (stek.value && stek.value.name === stekName) {
            showSelectStek.value = false;
            return;
        }

        // If there are no SuperSteks redirect to the creation page, unless
        // we're currently loading the steks.
        if (steks.value.length === 0) {
            router.replace({
                name: "createStek",
                params: {
                    orgName: route.params.orgName,
                },
            });
            stek.value = undefined;
            showSelectStek.value = false;
            return;
        }

        if (steks.value.length === 1) {
            stek.value = steks.value[0];
        } else {
            stek.value = steks.value.find((stek) => stek.name === stekName);
        }

        if (!stek.value) {
            showSelectStek.value = true;
            return;
        }

        // When stekName is undefined while a stek is selected, update
        // the url to include the selected stek. (this can happen when there
        // is only one stek, in which case the user won't be shown the stek
        // picker where clicking a stek updates the url.)
        if (!stekName && organization.value && route.meta.requiresStek) {
            router.replace({
                name: "stekPage",
                params: {
                    orgName: organization.value.name,
                    stekName: stek.value.name,
                },
            });
        }

        showSelectStek.value = false;
        try {
            await fetchStek();
        } catch (error) {
            console.error("Failed to fetch stek:", error);
            // Continue showing the stek page even if logo or other details failed to load
            // The basic information is already available in stek.value
            toast.add({
                severity: "warn",
                summary: "Some Stek details could not be loaded",
                detail: "Using cached information instead",
                life: 5000,
            });
        }
    }

    /**
     * Deletes the current Stek.
     */
    async function remove(opts: { showToast: boolean } = { showToast: true }) {
        if (!stek.value) {
            console.warn("Tried to delete stek, but no stek active.");
            return;
        }
        if (!organization.value) {
            console.warn("Tried to delete stek, but no organization active.");
            return;
        }

        const { response } = await api.startStekStekMgtDeleteStekStartPost({});

        if (response.ok) {
            if (opts.showToast) {
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: `Stek ${stek.value.name} deleted`,
                });
            }
            await getSteks();
        } else {
            if (opts.showToast) {
                toast.add({
                    severity: "error",
                    summary: "Could not delete " + stek.value.name,
                    detail: response.status,
                });
            }
        }
    }

    const queueSave = async () => {
        if (!isModified.value || !stek.value) {
            return;
        }

        const updateContent: types.StartStekStekMgtUpdateStekStartPostData = {
            body: {
                stek_id: stek.value.identity,
            },
        };
        if (logo.value?.isModified) {
            updateContent.body!.logo = logo.value.blob;
        }

        if (fqdn.value !== originalFqdn.value) {
            updateContent.body!.fqdn = fqdn.value;
        }

        if (Object.keys(updateContent.body!).length === 0) {
            return;
        }

        useSyncStore().queueSync({
            entityType: EntityType.STEK,
            args: [updateContent],
        });

        if (logo.value?.isModified) {
            logo.value.persist();
        }

        originalFqdn.value = fqdn.value;
    };

    const fetchStek = async () => {
        const stekId = stekInfo.value?.identity;

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        // If no stek id is set
        if (!stekId) {
            logo.value = undefined;
            stek.value = undefined;
            return;
        } else {
            try {
                await Promise.all([fetchLogo(stekId)]);

                // Update FQDN from stek data, which should already be loaded via steks store
                fqdn.value = stek.value?.fqdn || "";
                originalFqdn.value = fqdn.value;
            } catch (error) {
                console.error("Error fetching Stek details:", error);
                // Still keep the page usable with whatever data we have
                // Logo might be undefined, but that's handled in the template
                throw error;
            }
        }
    };

    const fetchLogo = async (currentStekId?: string | undefined) => {
        if (stek.value) {
            if (stek.value.logo) {
                isFetchingLogo.value = true;
                try {
                    const { response, data } = await followLink<File>(
                        stek.value.logo,
                        "get",
                        undefined,
                        controller.value.signal
                    );

                    if (!response.ok) {
                        if (response.status === 404) {
                            // Not found is ok, we'll use the placeholder
                            return;
                        }
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }

                    // Check if stek has changed during the async operation
                    if (!stek.value || stek.value.identity !== currentStekId) {
                        console.debug(
                            "Stek changed during logo fetch, aborting logo update"
                        );
                        return;
                    }

                    // Try to create the artefact, but catch errors to prevent breaking the UI
                    try {
                        logo.value = await Artefact.createBpmnArtefact({
                            name: "stekLogo",
                            identity: stek.value.logo.identity,
                            blob: data!,
                        });

                        // Check again if stek has changed during artefact creation
                        if (
                            !stek.value ||
                            stek.value.identity !== currentStekId
                        ) {
                            console.debug(
                                "Stek changed during artefact creation, aborting logo update"
                            );
                            return;
                        }

                        logo.value.links = stek.value.logo.links;
                    } catch (artefactError) {
                        console.error(
                            "Failed to create logo artefact:",
                            artefactError
                        );
                        // If creating the artefact fails, keep using the placeholder
                        // This prevents the UI from breaking if logo processing fails
                    }
                } catch (e) {
                    if (e instanceof DOMException && e.name === "AbortError") {
                        // Aborted request, this is normal when navigating away
                        return;
                    } else {
                        // Log the error but don't throw - let the UI handle fallback
                        console.error("Error fetching logo:", e);
                        // Will use the placeholder logo from the watch handler
                    }
                } finally {
                    isFetchingLogo.value = false;
                }
            } else {
                // Check if stek has changed before setting an empty artefact
                if (!stek.value || stek.value.identity !== currentStekId) {
                    console.debug(
                        "Stek changed before creating empty artefact, aborting"
                    );
                    return;
                }

                // Create an empty artefact for cases where no logo exists
                try {
                    logo.value = Artefact.createEmptyArtefact({
                        mimetype: "application/bpmn+xml",
                        identity: stek.value.identity + "_logo",
                    });
                } catch (error) {
                    console.error(
                        "Failed to create empty logo artefact:",
                        error
                    );
                    // Fallback will show placeholder logo from the watch handler
                }
            }
        }
    };

    watch(
        () => route.meta.requiresStek,
        (newRequiresStek) => {
            if (!newRequiresStek) {
                stek.value = undefined;
            } else {
                selectStekFromSteks();
            }
        },
        { immediate: true }
    );

    // When the list of steks changes, try to select a stek.
    watch(
        steks,
        () => {
            selectStekFromSteks();
        },
        { deep: true, immediate: true }
    );

    // When the stekName in the pathname changes, try to select the new
    // stek using the new stekName.
    watch(
        () => stekInfo.value?.identity,
        (newValue, oldValue) => {
            // Set the header for the WebComponents requests lib.
            headerManager.updateHeaders({
                stek: newValue || "",
            });

            const selectedOrganization = organization.value;
            if (!selectedOrganization) {
                return;
            }

            if (newValue !== oldValue) {
                selectStekFromSteks();
                // Force update when identity changes.
                fetchStek();
            }
        },
        { immediate: true }
    );

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.STEK &&
                identity.entityId === stek.value?.identity
            ) {
                fetchStek();
            }
        }
    );

    return {
        logo,
        fqdn,
        showSelectStek,
        isModified,
        isLoadingSteks,
        stek,
        remove,
        isFetching,
    };
});

export const useStekManager = () => storeToRefs(useStekStore());
